import {
  getTimeZoneDateParts,
  type WallClockTime,
  zonedStartOfDayToUtc,
} from '../../../assets/helpers/utils/time-zone-date.ts';
import type { IsoDate } from '../../types.ts';
import type { PerformanceBucketSize, PerformancePeriod } from '../performance-api.ts';

export type PeriodBucket = {
  key: string;
  start: Date;
};

export type PeriodRange = {
  from: Date;
  /** Inclusive end of the period. */
  to: Date;
  bucket: PerformanceBucketSize;
  buckets: PeriodBucket[];
};

const normalize = ({ year, month, day }: WallClockTime): WallClockTime => {
  const date = new Date(Date.UTC(year, month - 1, day));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
};

const getWallClockDate = (date: Date | IsoDate, timeZone: string): WallClockTime => {
  const parts = getTimeZoneDateParts(date, timeZone);

  if (!parts) {
    throw new Error(`Invalid date: ${String(date)}`);
  }

  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
};

const pad = (value: number) => String(value).padStart(2, '0');

const toBucketKey = (wallClock: WallClockTime, bucket: PerformanceBucketSize) =>
  bucket === 'day'
    ? `${wallClock.year}-${pad(wallClock.month)}-${pad(wallClock.day)}`
    : `${wallClock.year}-${pad(wallClock.month)}`;

export const getBucketKey = (date: IsoDate, bucket: PerformanceBucketSize, timeZone: string) =>
  toBucketKey(getWallClockDate(date, timeZone), bucket);

const getPeriodStart = (
  period: PerformancePeriod,
  today: WallClockTime
): { start: WallClockTime; bucket: PerformanceBucketSize } => {
  switch (period) {
    case 'this_month':
      return { start: { ...today, day: 1 }, bucket: 'day' };
    case 'last_month':
      return { start: { ...today, month: today.month - 1, day: 1 }, bucket: 'day' };
    case 'last_7_days':
      return { start: { ...today, day: today.day - 6 }, bucket: 'day' };
    case 'last_30_days':
      return { start: { ...today, day: today.day - 29 }, bucket: 'day' };
    case 'last_6_months':
      return { start: { ...today, month: today.month - 5, day: 1 }, bucket: 'month' };
  }
};

const getBuckets = (
  start: WallClockTime,
  to: Date,
  bucket: PerformanceBucketSize,
  timeZone: string
): PeriodBucket[] => {
  const buckets: PeriodBucket[] = [];
  let current = normalize(start);
  let currentStart = zonedStartOfDayToUtc(current, timeZone);

  while (currentStart <= to) {
    buckets.push({ key: toBucketKey(current, bucket), start: currentStart });
    current =
      bucket === 'day'
        ? normalize({ ...current, day: current.day + 1 })
        : normalize({ ...current, month: current.month + 1 });
    currentStart = zonedStartOfDayToUtc(current, timeZone);
  }

  return buckets;
};

export const getPeriodRange = (
  period: PerformancePeriod,
  timeZone: string,
  now: Date
): PeriodRange => {
  const today = getWallClockDate(now, timeZone);
  const { start, bucket } = getPeriodStart(period, today);
  const from = zonedStartOfDayToUtc(normalize(start), timeZone);
  const to =
    period === 'last_month'
      ? new Date(zonedStartOfDayToUtc({ ...today, day: 1 }, timeZone).getTime() - 1)
      : now;

  return { from, to, bucket, buckets: getBuckets(start, to, bucket, timeZone) };
};
